import { createSelector } from 'reselect';
import { Subjects, Grades } from './aToF.constants';

const getProfile = (state) => state.modules.Auth.auth.profile;
const getSchoolList = (state) => state.modules.Settings.schoolSettings.schoolList;
const getCampusGroups = (state) => state.modules.Settings.campusGroups.groups;

// Early-access Campus Groups (spec §7b): flagged district admins only, no
// isSuperAdmin bypass. Groups render between the District entry and the
// campuses; option values use the "group:{groupId}" wire format.
// Campus dropdown order: by grade span (EE/PK/KG first, then numeric gradeStart), name as tie-break,
// so a district reads El -> Int/Middle -> HS instead of alphabetically (where "H S" beats "Intermediate").
const gradeRank = (g) => {
  const s = String(g || '').toUpperCase();
  if (s === 'EE') return -3;
  if (s === 'PK') return -2;
  if (s === 'KG' || s === 'K') return -1;
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? 99 : n;
};
const byGradeSpanThenName = (schoolList) => ([aNum, aName], [bNum, bName]) => {
  const diff =
    gradeRank(schoolList[aNum] && schoolList[aNum].gradeStart) -
    gradeRank(schoolList[bNum] && schoolList[bNum].gradeStart);
  return diff !== 0 ? diff : aName.localeCompare(bName);
};

export const hasCampusGroupsAccess = (profile) =>
  !!profile &&
  profile.role === 'districtAdmin' &&
  !!(profile.featureFlags && profile.featureFlags.campusGroups === true);

export const schoolListPlusDistrict = createSelector(
  getProfile,
  getSchoolList,
  getCampusGroups,
  (profile, schoolList, campusGroups) => {
    let schools = Object.entries(schoolList).reduce((prev, [campusNumber, campus]) => {
      prev[campusNumber] = campus.name;
      return prev;
    }, {});
    delete schools[profile.districtNumber];
    schools = Object.entries(schools).sort(byGradeSpanThenName(schoolList));
    if (profile && profile.role === 'districtAdmin') {
      if (schools.length > 1) {
        schools.unshift([profile.districtNumber, 'District']);
      }
    }
    if (hasCampusGroupsAccess(profile) && campusGroups && Object.keys(campusGroups).length) {
      const groupEntries = Object.entries(campusGroups)
        .map(([groupId, group]) => [`group:${groupId}`, group.name || 'Campus Group', 'campusGroup'])
        .sort((a, b) => a[1].localeCompare(b[1]));
      const insertAt = schools.length && schools[0][0] === profile.districtNumber ? 1 : 0;
      schools.splice(insertAt, 0, ...groupEntries);
    }
    return schools;
  }
);

const getFilterFields = (state) => state.modules.AtoF.aToF.filterFields;

export const selectFilterFields = createSelector(getFilterFields, (filterFields) => {
  if (filterFields) {
    let { subjects, grades, teachers, tags } = filterFields;
    if (subjects && subjects.length) {
      subjects = subjects.sort((sub1, sub2) => sub1.localeCompare(sub2));
    }
    const formattedSubjects = [];
    (subjects || []).forEach((subject) => {
      formattedSubjects.push({
        value: subject,
        label: Subjects[subject],
      });
    });
    const formattedGrades = [];
    if (grades && grades.length) {
      grades = grades.sort((grade1, grade2) => grade1 - grade2);
    }
    (grades || []).forEach((grade) => {
      formattedGrades.push({
        value: grade,
        label: Grades[grade],
      });
    });
    const formattedTeachers = [];
    if (teachers && teachers.length) {
      teachers = teachers.sort((t1, t2) => t1.localeCompare(t2));
    }
    (teachers || []).forEach((teacher) => {
      formattedTeachers.push({
        value: teacher,
        label: teacher,
      });
    });
    const formattedTags = [];
    if (tags && tags.length) {
      tags = tags.sort((t1, t2) => t1.localeCompare(t2));
    }
    (tags || []).forEach((tag) => {
      formattedTags.push({
        value: tag,
        label: tag,
      });
    });
    return {
      subjects: formattedSubjects,
      grades: formattedGrades,
      teachers: formattedTeachers,
      tags: formattedTags,
    };
  }
  return {
    subjects: [],
    grades: [],
    teachers: [],
    tags: [],
  };
});

export default 'assessment.selectors.js';
